import {
  Box,
  Container,
  Heading,
  HStack,
  Icon,
  Link,
  Text,
  VStack,
} from "@chakra-ui/react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { Icons } from "../../WinterCountIconFIle.js";

const MotionBox = motion(Box);

const features = [
  {
    icon: Icons.HealthPotion,
    stat: "Monitor",
    statSub: "Health",
    title: "Health Monitoring",
    description:
      "Access Dashboards and Analysis designed for Tribal Health Directors. View timely, reliable data on priority conditions, trends, and interventions to support informed leadership decisions and community health planning.",
    color: "green.400",
    colorHex: "#48BB78",
    link: "/Monitor",
  },
  {
    icon: Icons.InvestigateCase,
    stat: "Investigate ",
    statSub: "Cases",
    title: "Case Investigations",
    description:
      "Access the Electronic Investigation System (EIS) to use data from multiple sources for case investigation, coordinated response, and informed decision-making to support the health of the Great Plains Tribes communities.",
    color: "orange.400",
    colorHex: "#ED8936",
    link: "/Investigate",
  },
  {
    icon: Icons.AdditionalDataIcon,
    stat: "Additional",
    statSub: "Data",
    title: "Additional Data",
    description:
      "Access additional data to explore community-level datasets such as Census, crime, and other non-personal indicators. These resources support population-level planning and prevention as well as grant requests and reporting.",
    color: "purple.400",
    colorHex: "#9F7AEA",
    link: "/Monitor?tab=Additional Data Resources",
  },
  {
    icon: Icons.OutSideReportIcon,
    stat: "Source",
    statSub: "Reports",
    title: "Outside Reports",
    description:
      "Access Partner Agency Sources to view raw reports directly from reporting systems, including laboratory, case, and other feeds.",
    color: "blue.400",
    colorHex: "#63B3ED",
    link: "/Source",
  },
  {
    icon: Icons.PublicHealthIcon,
    stat: "Public Health",
    statSub: "References",
    title: "Public Health References",
    description:
      "Reference CDC case definitions, investigation guidance, and public health standards to support accurate reporting and shared understanding.",
    color: "teal.400",
    colorHex: "#4FD1C5",
    link: "/Monitor?tab=External Resources",
  },
];

// Split features for layout: first 2 go right of image, rest go in bottom row
const topFeatures = features.slice(0, 2);
const bottomFeatures = features.slice(2);

const StatCard = ({ feature, index, isTop = false }) => {
  const navigate = useNavigate();

  const cardVariants = {
    hidden: { opacity: 0, x: isTop ? 80 : 0, y: isTop ? 0 : 60 },
    visible: {
      opacity: 1,
      x: 0,
      y: 0,
      transition: {
        duration: 0.7,
        delay: index * 0.15,
        ease: [0.25, 0.46, 0.45, 0.94],
      },
    },
    hover: {
      y: -6,
      transition: { duration: 0.3, ease: "easeInOut" },
    },
  };

  return (
    <MotionBox
      variants={cardVariants}
      initial="hidden"
      whileInView="visible"
      whileHover="hover"
      viewport={{ once: true, margin: "-80px" }}
      bg="rgba(30, 40, 55, 0.92)"
      borderRadius="xl"
      p={5}
      cursor="pointer"
      onClick={() => navigate(feature.link)}
      position="relative"
      overflow="hidden"
      borderLeft="4px solid"
      borderColor={feature.color}
      _hover={{
        boxShadow: `0 8px 40px ${feature.colorHex}33`,
        bg: "rgba(35, 48, 65, 0.98)",
      }}
      transition="background 0.3s ease, box-shadow 0.3s ease"
      flex={isTop ? "1" : "1"}
    >
      {/* Stat / Icon area */}
      <HStack align="flex-start" spacing={2} minW="120px">
        <Box
          mb={2}
          p={2}
          borderRadius="lg"
          bg={`${feature.color.split(".")[0]}.900`}
          color={feature.color}
        >
          <Icon as={feature.icon} boxSize={6} />
        </Box>
        <Box>
          <Text
            fontSize="xl"
            fontWeight="800"
            color={feature.color}
            lineHeight="1"
            fontFamily="'Georgia', serif"
            letterSpacing="-0.5px"
          >
            {feature.stat}
          </Text>
          <Text
            fontSize="md"
            letterSpacing="2px"
            fontWeight="700"
            color="whiteAlpha.800"
            lineHeight="1.3"
            mt={1}
          >
            {feature.statSub}
          </Text>
        </Box>
      </HStack>

      {/* Description area */}
      <VStack align="flex-start" spacing={3} flex="1">
        <Text color="whiteAlpha.800" fontSize="sm" lineHeight="1.7" flex="1">
          {feature.description}
        </Text>
        <Link
          fontSize="sm"
          fontWeight="700"
          color={feature.color}
          textDecoration="underline"
          textUnderlineOffset="3px"
          _hover={{ color: "white" }}
          onClick={(e) => {
            e.stopPropagation();
            navigate(feature.link);
          }}
        >
          Learn More
        </Link>
      </VStack>
    </MotionBox>
  );
};

const AboutSection = () => {
  const navigate = useNavigate();

  const headerVariants = {
    hidden: { opacity: 0, y: -30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.8, ease: "easeOut" },
    },
  };

  const imageVariants = {
    hidden: { opacity: 0, x: -80 },
    visible: {
      opacity: 1,
      x: 0,
      transition: { duration: 0.9, ease: [0.25, 0.46, 0.45, 0.94] },
    },
  };

  // const bottomCardVariants = (index) => ({
  //   hidden: { opacity: 0, y: 60 },
  //   visible: {
  //     opacity: 1,
  //     y: 0,
  //     transition: {
  //       duration: 0.7,
  //       delay: index * 0.15,
  //       ease: [0.25, 0.46, 0.45, 0.94],
  //     },
  //   },
  //   hover: {
  //     y: -6,
  //     transition: { duration: 0.3, ease: "easeInOut" },
  //   },
  // });

  return (
    <Box
      position="relative"
      bg="navy.900"
      sx={{
        background: "linear-gradient(180deg, #0d1b2a 0%, #0a1520 100%)",
      }}
      pt={1}
      pb={5}
    >
      <Container maxW="7xl" position="relative" zIndex={2}>
        {/* Header */}
        <MotionBox
          variants={headerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          mb={6}
          textAlign="center"
        ></MotionBox>

        {/* Top Section: Image Left + 2 Cards Right */}
        <Box
          display="flex"
          flexDir={{ base: "column", md: "row" }}
          gap={4}
          mb={4}
          alignItems="stretch"
        >
          {/* Left: Large Image with Title */}
          <MotionBox
            variants={imageVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            flex="1.1"
            borderRadius="xl"
            overflow="hidden"
            position="relative"
            minH={{ base: "280px", md: "auto" }}
          >
            <Box
              as="img"
              src="https://www.greatplainstribalhealth.org/wp-content/uploads/2025/12/badlands-hero-01.webp"
              alt="Badlands landscape"
              width="100%"
              height="100%"
              objectFit="cover"
              display="block"
            />
            {/* Title overlay */}
            <Box
              position="absolute"
              top={0}
              left={0}
              right={0}
              bottom={0}
              bg="linear-gradient(135deg, rgba(0,0,0,0.65) 0%, rgba(0,0,0,0.1) 60%)"
            />
            <Box position="absolute" top={6} left={6}>
              <Heading
                as="h3"
                fontSize={{ base: "2xl", md: "3xl" }}
                fontWeight="800"
                color="#F1AC27"
                fontFamily="'Georgia', serif"
                lineHeight="1.15"
                textShadow="2px 2px 8px rgba(0,0,0,0.8)"
              >
                About us
              </Heading>
              <Text fontSize="md" color="#FFFFFF" maxW="3xl" mx="auto">
                <b>Winter Count</b> is a modern public health data and case
                investigation system built by the Great Plains Tribal
                Epidemiology Center (GPTEC), to strengthen Tribal health
                leadership and advance data sovereignty across the Great Plains.
              </Text>
            </Box>
          </MotionBox>

          {/* Right: 2 stacked cards */}
          <VStack flex="1" spacing={4} align="stretch">
            {topFeatures.map((feature, index) => (
              <StatCard key={index} feature={feature} index={index} isTop />
            ))}
          </VStack>
        </Box>

        {/* Bottom Row: 3 Cards */}
        <Box display="flex" flexDir={{ base: "column", md: "row" }} gap={4}>
          {bottomFeatures.map((feature, index) => (
            <MotionBox
              key={index}
              // variants={bottomCardVariants(index)}
              initial="hidden"
              whileInView="visible"
              whileHover="hover"
              viewport={{ once: true, margin: "-80px" }}
              flex="1"
              bg="rgba(30, 40, 55, 0.92)"
              borderRadius="xl"
              p={5}
              cursor="pointer"
              onClick={() => navigate(feature.link)}
              position="relative"
              overflow="hidden"
              borderLeft="4px solid"
              borderColor={feature.color}
              _hover={{
                boxShadow: `0 8px 40px ${feature.colorHex}33`,
                bg: "rgba(35, 48, 65, 0.98)",
              }}
              transition="background 0.3s ease, box-shadow 0.3s ease"
            >
              <VStack align="flex-start" spacing={3}>
                <HStack spacing={3}>
                  <Box
                    p={2}
                    borderRadius="lg"
                    bg={`${feature.color.split(".")[0]}.900`}
                    color={feature.color}
                  >
                    <Icon as={feature.icon} boxSize={5} />
                  </Box>
                  <VStack align="flex-start" spacing={0}>
                    <Text
                      fontSize="xl"
                      fontWeight="800"
                      color={feature.color}
                      lineHeight="1"
                      fontFamily="'Georgia', serif"
                    >
                      {feature.stat}
                    </Text>
                    <Text
                      fontSize="md"
                      fontWeight="700"
                      color="whiteAlpha.800"
                      lineHeight="1.2"
                      letterSpacing="2px"
                    >
                      {feature.statSub}
                    </Text>
                  </VStack>
                </HStack>
                <Text color="whiteAlpha.800" fontSize="sm" lineHeight="1.7">
                  {feature.description}
                </Text>
                <Link
                  fontSize="sm"
                  fontWeight="700"
                  color={feature.color}
                  textDecoration="underline"
                  textUnderlineOffset="3px"
                  _hover={{ color: "white" }}
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(feature.link);
                  }}
                >
                  Learn More
                </Link>
              </VStack>
            </MotionBox>
          ))}
        </Box>
      </Container>
    </Box>
  );
};

export default AboutSection;
